import type { OnboardingAnswers, OnboardingStep } from './useOnboardingMachine'

// Bumped from v2 — the FRONTEND-N demo-flow rework (single-select prompt,
// choosing_platform/reviewing_response/confirming_competitors steps,
// DemoResult shape change) is a breaking change to the persisted answers
// shape. A user mid-onboarding on the old shape must get a clean restart,
// not a malformed restore into a step/answers combination this version's
// reducer/rebuildTranscript don't understand.
const STORAGE_PREFIX = 'lensora_onboarding_v3'
// Exported so auth-provider's sign-out/sign-in cache cleanup can clear it
// too — this key predates the chat onboarding rework (it was the old
// linear-wizard OnboardingGate's storage key) but the auth provider's own
// cleanup list needs a stable import path independent of which onboarding
// UI currently owns the concept.
export const ONBOARDING_STORAGE_KEY = 'lensora_onboarding_state'
const LEGACY_STORAGE_KEY = ONBOARDING_STORAGE_KEY
const PENDING_WEBSITE_KEY = 'lensora_pending_website'

interface StoredOnboardingState {
  version: 3
  profileId: string
  state: OnboardingStep
  websiteId: string | null
  answers: OnboardingAnswers
}

function storageKey(profileId: string): string {
  return `${STORAGE_PREFIX}:${profileId}`
}

// One-time migration cleanup — the v1 shape wasn't namespaced per user, so a
// shared browser could let a second user resume the first user's in-progress
// state (including their websiteId). Wipe it outright rather than migrate it.
export function clearLegacyOnboardingState(): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    /* ignore — private browsing / storage disabled */
  }
}

export function loadOnboardingState(profileId: string): Pick<StoredOnboardingState, 'state' | 'websiteId' | 'answers'> | null {
  try {
    const raw = localStorage.getItem(storageKey(profileId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredOnboardingState
    if (parsed.version !== 3 || parsed.profileId !== profileId) return null
    return { state: parsed.state, websiteId: parsed.websiteId, answers: parsed.answers }
  } catch {
    return null
  }
}

export function saveOnboardingState(
  profileId: string,
  data: { state: OnboardingStep; websiteId: string | null; answers: OnboardingAnswers }
): void {
  try {
    const payload: StoredOnboardingState = { version: 3, profileId, ...data }
    localStorage.setItem(storageKey(profileId), JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

export function clearOnboardingState(profileId: string): void {
  try {
    localStorage.removeItem(storageKey(profileId))
  } catch {
    /* ignore */
  }
}

// Called pre-auth from the marketing site's hero form hand-off, before a
// profileId exists — intentionally unnamespaced.
export function stashPendingWebsite(websiteUrl: string): void {
  try {
    localStorage.setItem(PENDING_WEBSITE_KEY, websiteUrl)
  } catch {
    /* ignore */
  }
}

// Consumed exactly once — read and immediately delete — when the wizard
// reaches asking_website, so it only ever prefills a single session.
export function consumePendingWebsite(): string | null {
  try {
    const value = localStorage.getItem(PENDING_WEBSITE_KEY)
    if (value) localStorage.removeItem(PENDING_WEBSITE_KEY)
    return value
  } catch {
    return null
  }
}
